import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ChevronDown,
  ClipboardList,
  FlaskConical,
  RefreshCw,
  SettingsIcon,
  Sparkles,
  Telescope,
} from 'lucide-react';
import { type JSX, type ReactNode } from 'react';

import { ensureQualityEnabled } from '@/features/quality/guard.js';
import { GenerateTestsPanel } from '@/features/quality/GenerateTestsPanel.js';
import { ExploratoryTestPanel } from '@/features/quality/ExploratoryTestPanel.js';
import { HintCard } from '@/features/onboarding/HintCard.js';
import { useHintTarget } from '@/features/onboarding/useHintTarget.js';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.js';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { QualityRegression } from '@/features/quality/QualityRegression.js';
import { QualityRuns } from '@/features/quality/QualityRuns.js';
import { QualityTests } from '@/features/quality/QualityTests.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import type { QualityHelpTopic } from '@/features/quality/help/quality-guide-map.js';
import { SetupEmptyActions } from '@/features/quality/setup/SetupEmptyActions.js';
import { openSetupInAgent } from '@/features/quality/setup/openSetupInAgent.js';
import { workspaceProject } from '@/features/quality/workspace-project.js';
import { qualityProjectsQueryOptions } from '@/lib/api/quality.js';
import type { Project } from '@/lib/api/quality-types.js';
import { PageContainer } from '@/ui/PageContainer.js';

/** Sentinel for the legacy "All projects" value — kept out of the URL (undefined `projectId`). */
const ALL = '__all__';

type QualityTab = 'tests' | 'runs' | 'regression' | 'generate' | 'exploratory';
const TABS: readonly QualityTab[] = [
  'tests',
  'runs',
  'regression',
  'generate',
  'exploratory',
];

export interface QualitySearch {
  /** Active tab — kept in the URL so deep links / back-button work. */
  tab?: QualityTab;
  /**
   * Legacy per-project deep link (e.g. the command palette → a project). The listing tabs
   * (Tests / Runs) now scope by the globally-selected workspace, not this id; only the
   * project-scoped action tabs (Generate / Exploratory) still honour it.
   */
  projectId?: string;
  /**
   * "Edit and run again" (F11): the Generate tab prefills its form from this run's own
   * configuration instead of the project's last request. Set only by that action, on the
   * generate tab.
   */
  prefillRunId?: string;
}

export const Route = createFileRoute('/quality')({
  component: QualityPage,
  beforeLoad: () => ensureQualityEnabled(),
  validateSearch: (raw: Record<string, unknown>): QualitySearch => ({
    tab: TABS.includes(raw.tab as QualityTab)
      ? (raw.tab as QualityTab)
      : undefined,
    // Trim and reject the reserved sentinel so `?projectId=__all__` / whitespace can't masquerade as
    // a real project filter — those all normalize to undefined.
    projectId: normalizeProjectId(raw.projectId),
    prefillRunId:
      typeof raw.prefillRunId === 'string' && raw.prefillRunId.trim()
        ? raw.prefillRunId.trim()
        : undefined,
  }),
});

function normalizeProjectId(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed && trimmed !== ALL ? trimmed : undefined;
}

/**
 * Shown on the project-scoped action tabs when a concrete workspace is selected but no quality project
 * is linked to it — otherwise those tabs would act on no project silently.
 */
function NoLinkedProjectNotice({
  workspaceId,
  topic,
}: {
  workspaceId: string;
  /** The tab's guide topic, for the guide link beside "Set up in chat". */
  topic: QualityHelpTopic;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border px-6 py-12 text-center">
      <p className="text-sm text-muted-foreground">
        No quality project is linked to this workspace. Enable Quality in
        workspace settings, or wait for provisioning.
      </p>
      <SetupEmptyActions topic={topic}>
        <QualityManageSettingsLink
          workspaceId={workspaceId}
          label="Workspace Quality settings"
        />
      </SetupEmptyActions>
    </div>
  );
}

/**
 * Shortcut to this workspace's Quality manage tab (`/workspaces/:id/manage?tab=quality`),
 * matching the Developer page's Settings button. The settings form itself stays on that tab.
 */
function QualityManageSettingsLink({
  workspaceId,
  label,
}: {
  workspaceId: string;
  label: string;
}): JSX.Element {
  return (
    <Button variant="outline" size="sm" asChild>
      <Link
        to="/workspaces/$id/manage"
        params={{ id: workspaceId }}
        search={{ tab: 'quality' }}
      >
        <SettingsIcon className="size-4" />
        {label}
      </Link>
    </Button>
  );
}

/**
 * Resolve the project the project-scoped action tabs (Generate / Exploratory) should act on. A
 * workspace maps 1:1 to a quality project, so the current project is the one linked
 * to the selected workspace, picked by `workspaceProject` when several are linked. A valid
 * URL `?projectId=` deep link is accepted only when that project belongs to the selected workspace.
 * Returns `undefined` when neither a linked project nor a workspace-scoped deep link resolves.
 */
export function resolveActiveProjectId(
  projects: Project[],
  selectedWorkspaceId: string | null,
  urlProjectId: string | undefined,
): string | undefined {
  const linkedProjectId = selectedWorkspaceId
    ? workspaceProject(projects, selectedWorkspaceId)?.id
    : undefined;
  const validUrlProjectId =
    urlProjectId &&
    selectedWorkspaceId &&
    projects.some(
      (project) =>
        project.id === urlProjectId &&
        project.workspace_id === selectedWorkspaceId,
    )
      ? urlProjectId
      : undefined;
  return linkedProjectId ?? validUrlProjectId;
}

/**
 * Guard "Edit and run again" (F11) against a resolved project it wasn't launched for.
 * `QualityRunDetail` always sets `urlProjectId` to the prefilled run's own project — so honour
 * `prefillRunId` only when that matches what `resolveActiveProjectId` actually resolved. A
 * workspace-linked project can otherwise outrank a stale `?projectId=` link (see
 * `resolveActiveProjectId`), and applying the prefill anyway would render one project's run
 * configuration on another project's form.
 */
export function resolveEffectivePrefillRunId(
  urlProjectId: string | undefined,
  effectiveProjectId: string | undefined,
  prefillRunId: string | undefined,
): string | undefined {
  return urlProjectId != null && urlProjectId === effectiveProjectId
    ? prefillRunId
    : undefined;
}

interface HintedQualityTabsTriggerProps {
  hintId: string;
  value: QualityTab;
  children: ReactNode;
}

/**
 * Quality action-tab trigger with an onboarding beacon. Own component because `useHintTarget`
 * cannot be called inside a map. Path mode: the beacon marks the tab; the click still switches.
 */
function HintedQualityTabsTrigger({
  hintId,
  value,
  children,
}: HintedQualityTabsTriggerProps): JSX.Element {
  const hintRef = useHintTarget<HTMLButtonElement>(hintId, 'path');

  return (
    <TabsTrigger ref={hintRef} value={value}>
      {children}
    </TabsTrigger>
  );
}

/**
 * The `/quality` header's way into guided setup, available whatever the project's state. While the
 * agent tips are hidden it becomes a menu that also brings them back, since there is no user
 * settings page to put that switch on.
 */
function QualitySetupButton(): JSX.Element {
  const navigate = useNavigate();
  const tipsDismissed = useUiStore((state) => state.qualityAgentTipsDismissed);
  const showTips = useUiStore((state) => state.setQualityAgentTipsDismissed);
  const startSetup = (): void =>
    openSetupInAgent({ entry: 'quality-header', navigate });
  if (!tipsDismissed) {
    return (
      <Button variant="outline" size="sm" onClick={startSetup}>
        <Sparkles className="size-4" />
        Set up in chat
      </Button>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Sparkles className="size-4" />
          Set up in chat
          <ChevronDown className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={startSetup}>
          Set up in chat
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => showTips(false)}>
          Show agent tips
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The one-time offer on an unconfigured project. Closing it is remembered per browser, so the offer
 * is made on the first visit and not on every one after.
 */
function QualitySetupOffer(): JSX.Element | null {
  const navigate = useNavigate();
  const dismissed = useUiStore((state) => state.qualitySetupOfferDismissed);
  const dismiss = useUiStore((state) => state.setQualitySetupOfferDismissed);
  if (dismissed) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary bg-primary/5 px-4 py-3">
      <span className="text-sm text-foreground">
        This project has no test source yet. Want the agent to set it up with
        you? It takes about 5 minutes.
      </span>
      <span className="flex gap-2">
        <Button
          variant="brand"
          size="sm"
          onClick={() => {
            dismiss(true);
            openSetupInAgent({ entry: 'enable-banner', navigate });
          }}
        >
          Set up in chat
        </Button>
        <Button variant="ghost" size="sm" onClick={() => dismiss(true)}>
          Later
        </Button>
      </span>
    </div>
  );
}

function QualityPage(): JSX.Element {
  const navigate = Route.useNavigate();
  const { tab, projectId, prefillRunId } = Route.useSearch();
  const active = tab ?? 'tests';

  // The globally-selected workspace (sidebar switcher) is the single scope control for the listing
  // tabs — the old in-page project selector is gone (it duplicated the switcher).
  const selectedWorkspaceId = useUiStore((s) => s.selectedWorkspaceId);

  // The project-scoped action tabs (Generate / Exploratory) still act on one
  // project. With workspace ↔ project 1:1 we resolve it from the selected workspace, so those tabs
  // work straight from the tab bar without a URL param; a valid `?projectId=` deep link is the fallback.
  const projectsQuery = useQuery(qualityProjectsQueryOptions());
  const projects = projectsQuery.data ?? [];
  const effectiveProjectId = resolveActiveProjectId(
    projects,
    selectedWorkspaceId,
    projectId,
  );
  const authoringProjectMismatch = Boolean(
    projectId && projectId !== effectiveProjectId,
  );
  const effectivePrefillRunId = resolveEffectivePrefillRunId(
    projectId,
    effectiveProjectId,
    prefillRunId,
  );
  // A workspace is selected but nothing maps to it — the action tabs have no project to act on. The
  // null-workspace case can't reach here (Gap 2 always resolves a personal workspace).
  const noLinkedProject =
    selectedWorkspaceId != null &&
    !projectsQuery.isPending &&
    effectiveProjectId === undefined;
  /** The workspace's project exists but links no test source yet: guided setup is worth offering. */
  const unconfigured =
    projects.find((project) => project.id === effectiveProjectId)?.source_links
      ?.length === 0;

  return (
    <PageContainer
      maxWidth={active === 'generate' ? 'full' : '5xl'}
      className="gap-5 md:pb-16"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-[16rem] flex-1 flex-col gap-1">
          <h1 className="font-heading text-xl font-bold tracking-tight">
            Quality
          </h1>
          <p className="text-sm text-muted-foreground">
            Browse available test suites, launch automated runs, and track your
            results.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <QualitySetupButton />
          {selectedWorkspaceId ? (
            <QualityManageSettingsLink
              workspaceId={selectedWorkspaceId}
              label="Settings"
            />
          ) : null}
        </div>
      </div>

      {unconfigured ? <QualitySetupOffer /> : null}

      <Tabs
        value={active}
        onValueChange={(v) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              tab: TABS.includes(v as QualityTab) ? (v as QualityTab) : 'tests',
            }),
          })
        }
      >
        {/* h-auto: the list wraps on narrow screens; without it the fixed-height primitive would
            let a wrapped trigger overflow. Icons are monochrome (inherit the tab text colour) — the
            coloured family glyphs live only on the run cards. */}
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="tests">
            <ClipboardList /> Tests
          </TabsTrigger>
          <TabsTrigger value="runs">
            <FlaskConical /> Runs
          </TabsTrigger>
          <HintedQualityTabsTrigger
            hintId="quality.regression"
            value="regression"
          >
            <RefreshCw /> Regression
          </HintedQualityTabsTrigger>
          <HintedQualityTabsTrigger hintId="quality.generate" value="generate">
            <Sparkles /> Generate tests
          </HintedQualityTabsTrigger>
          <HintedQualityTabsTrigger
            hintId="quality.exploratory"
            value="exploratory"
          >
            <Telescope /> Exploratory test
          </HintedQualityTabsTrigger>
        </TabsList>

        <TabsContent value="tests">
          <QualityTests workspaceId={selectedWorkspaceId ?? undefined} />
        </TabsContent>
        <TabsContent value="runs">
          <QualityRuns workspaceId={selectedWorkspaceId ?? undefined} />
        </TabsContent>
        <TabsContent value="regression">
          <div className="space-y-4">
            <HintCard id="quality.regression" />
            {authoringProjectMismatch ? (
              <p role="alert" className="text-sm text-muted-foreground">
                Select the workspace for the requested project before
                continuing. No work has started.
              </p>
            ) : noLinkedProject ? (
              <NoLinkedProjectNotice
                workspaceId={selectedWorkspaceId!}
                topic="regression"
              />
            ) : (
              <QualityRegression projectId={effectiveProjectId} />
            )}
          </div>
        </TabsContent>
        {/* tabIndex={-1}: the panel has focusable children, so per the ARIA tabs pattern it need not
            be a tab stop itself. Radix defaults it to tabIndex={0}, which makes the browser scroll the
            (tall) panel's top into view when you click an input inside it — yanking the clicked field
            up out of view. Overriding to -1 removes that jump. */}
        <TabsContent value="generate" tabIndex={-1}>
          <div className="space-y-4">
            <HintCard id="quality.generate" />
            {authoringProjectMismatch ? (
              <p role="alert" className="text-sm text-muted-foreground">
                Select the workspace for the requested project before
                continuing. No work has started.
              </p>
            ) : noLinkedProject ? (
              <NoLinkedProjectNotice
                workspaceId={selectedWorkspaceId!}
                topic="generate-sources"
              />
            ) : (
              <GenerateTestsPanel
                projectId={effectiveProjectId}
                workspaceId={selectedWorkspaceId ?? undefined}
                prefillRunId={effectivePrefillRunId}
              />
            )}
          </div>
        </TabsContent>
        <TabsContent value="exploratory">
          <div className="space-y-4">
            <HintCard id="quality.exploratory" />
            {authoringProjectMismatch ? (
              <p role="alert" className="text-sm text-muted-foreground">
                Select the workspace for the requested project before
                continuing. No work has started.
              </p>
            ) : noLinkedProject ? (
              <NoLinkedProjectNotice
                workspaceId={selectedWorkspaceId!}
                topic="exploratory"
              />
            ) : (
              <ExploratoryTestPanel
                projectId={effectiveProjectId}
                workspaceId={selectedWorkspaceId ?? undefined}
              />
            )}
          </div>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
